import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { appConfirmMessage } from '../../../utils/appConfirm';
import {
  FaSearch,
  FaArrowDown,
  FaArrowUp,
  FaDownload,
  FaEye,
  FaEdit,
  FaTrash,
  FaWaveSquare,
  FaSyncAlt,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { useAdminListOverlay } from '../../../utils/useAdminPageSearch';
import StockRecordForm, { decodeMovementMeta } from './StockRecordForm';
import MovementDetailModal from './MovementDetailModal';
import '../styles/InventoryStock.css';

const FIND_ITEMS = `
  query StockMoveItems {
    findAllItem {
      id name code quantity available_quantity perItemCost unit supplier
    }
  }
`;

const FIND_PROJECTS = `
  query StockMoveProjects {
    findAllProject { id name }
  }
`;

const FIND_MOVEMENTS = `
  query StockMoveRecords {
    findAllInventoryManagement {
      id
      tool { id name code }
      project { id name }
      quantityAssigned
      assignedAt
      assignedBy
      remark
      updateAction
      category
      createdAt
    }
  }
`;

const REMOVE_MOVEMENT = `
  mutation RemoveStockMovement($id: String!) {
    removeInventoryManagement(id: $id) { id }
  }
`;

const PAGE_SIZE = 8;
const TABS = [
  { id: 'all', label: 'All Movements' },
  { id: 'IN', label: 'Stock In' },
  { id: 'OUT', label: 'Stock Out' },
  { id: 'ADJ', label: 'Adjustments' },
  { id: 'TRF', label: 'Transfers' },
];
const TAB_IDS = TABS.map((t) => t.id);

function classify(action) {
  const a = String(action || '').toLowerCase();
  if (a.includes('transfer')) return 'TRF';
  if (a.includes('adjust')) return 'ADJ';
  if (
    !a ||
    a.includes('add to stock') ||
    a.includes('stock in') ||
    a.includes('restock') ||
    a.includes('return')
  ) {
    return 'IN';
  }
  return 'OUT';
}

function movementRef(type, rec, index) {
  const n = String(index + 1).padStart(3, '0');
  if (type === 'TRF') return `TRF-${n}`;
  if (type === 'ADJ') return `AUD-${n}`;
  if (type === 'IN') return `IN-${n}`;
  if (rec.project?.name) return `OUT-${n}`;
  return '—';
}

function signedQty(type, qty, action) {
  const n = Number(qty) || 0;
  if (type === 'OUT') return -Math.abs(n);
  if (type === 'ADJ') {
    return String(action || '').toLowerCase().includes('increase')
      ? Math.abs(n)
      : -Math.abs(n);
  }
  if (type === 'TRF') return Math.abs(n);
  return Math.abs(n);
}

function modeFromType(type) {
  if (type === 'TRF') return 'transfer';
  if (type === 'ADJ') return 'adjust';
  if (type === 'IN') return 'in';
  return 'out';
}

function thisMonth(date) {
  if (!date) return false;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
}

function formatDate(raw) {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function movementCode(rec, index) {
  const d = new Date(rec.assignedAt || rec.createdAt || Date.now());
  const year = Number.isNaN(d.getTime()) ? new Date().getFullYear() : d.getFullYear();
  return `MOV-${year}-${String(index + 1).padStart(3, '0')}`;
}

function toEditShape(rec) {
  return {
    id: rec.id,
    inventoryManagementId: rec.id,
    item: rec.tool?.name || 'Unknown',
    code: rec.tool?.code || '',
    quantity: `${rec.quantityAssigned || 0} pcs`,
    quantityValue: rec.quantityAssigned || 0,
    project: rec.project?.name || '',
    projectId: rec.project?.id,
    category: rec.category || '',
    updateAction: rec.updateAction || 'Add to Stock',
    remark: rec.remark || '',
    itemId: rec.tool?.id,
    assignedBy: rec.assignedBy,
    date: rec.assignedAt || rec.createdAt,
  };
}

export default function StockMovementsDashboard() {
  const [records, setRecords] = useState([]);
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState('');
  const [site, setSite] = useState('All');
  const [month, setMonth] = useState('All');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [moveRes, itemRes, projectRes] = await Promise.all([
        graphqlRequest(FIND_MOVEMENTS),
        graphqlRequest(FIND_ITEMS).catch(() => ({})),
        graphqlRequest(FIND_PROJECTS).catch(() => ({})),
      ]);
      const list = Array.isArray(moveRes?.findAllInventoryManagement)
        ? [...moveRes.findAllInventoryManagement]
        : [];
      list.sort(
        (a, b) =>
          new Date(b.assignedAt || b.createdAt || 0) -
          new Date(a.assignedAt || a.createdAt || 0),
      );
      setRecords(list);
      setItems(itemRes?.findAllItem || []);
      setProjects(projectRes?.findAllProject || []);
    } catch (e) {
      toast.error(e?.message || 'Failed to load stock movements');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => {
    const chronological = [...records].sort(
      (a, b) =>
        new Date(a.assignedAt || a.createdAt || 0) -
        new Date(b.assignedAt || b.createdAt || 0),
    );
    const idMap = new Map();
    chronological.forEach((rec, i) => idMap.set(rec.id, movementCode(rec, i)));
    return records.map((rec) => {
      const type = classify(rec.updateAction);
      const qty = signedQty(type, rec.quantityAssigned, rec.updateAction);
      const { meta, note } = decodeMovementMeta(rec.remark);
      return {
        raw: rec,
        id: rec.id,
        code: idMap.get(rec.id),
        date: rec.assignedAt || rec.createdAt,
        type,
        product: rec.tool?.name || 'Unknown',
        sku: rec.tool?.code || '—',
        qty,
        site: meta.toSite || meta.site || rec.project?.name || 'Main Warehouse',
        employee: rec.assignedBy || '—',
        reference:
          meta.poNumber ||
          meta.jobNumber ||
          movementRef(type, rec, chronological.findIndex((r) => r.id === rec.id)),
        remark: note || meta.reason || rec.updateAction || '—',
        edit: toEditShape(rec),
      };
    });
  }, [records]);

  const overlay = useAdminListOverlay(rows, { allowedTabs: TAB_IDS });
  const tab = overlay.tab;
  const formMode =
    overlay.mode || (overlay.editing ? modeFromType(overlay.editing.type) : 'in');

  const sites = useMemo(
    () => ['All', ...Array.from(new Set(rows.map((r) => r.site))).sort()],
    [rows],
  );

  const months = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      const d = r.date ? new Date(r.date) : null;
      if (!d || Number.isNaN(d.getTime())) return;
      set.add(
        d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }),
      );
    });
    return ['All', ...Array.from(set)];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (tab !== 'all' && r.type !== tab) return false;
      if (site !== 'All' && r.site !== site) return false;
      if (month !== 'All') {
        const d = r.date ? new Date(r.date) : null;
        const label = d
          ? d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })
          : '';
        if (label !== month) return false;
      }
      if (!q) return true;
      return `${r.code} ${r.product} ${r.sku} ${r.employee} ${r.remark}`
        .toLowerCase()
        .includes(q);
    });
  }, [rows, tab, search, site, month]);

  useEffect(() => {
    setPage(1);
  }, [tab, search, site, month]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const pageRows = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  const kpis = useMemo(() => {
    const monthRows = rows.filter((r) => thisMonth(r.date));
    const inn = monthRows
      .filter((r) => r.type === 'IN')
      .reduce((s, r) => s + Math.abs(r.qty), 0);
    const out = monthRows
      .filter((r) => r.type === 'OUT')
      .reduce((s, r) => s + Math.abs(r.qty), 0);
    const adj = monthRows.filter((r) => r.type === 'ADJ').length;
    return { inn, out, net: inn - out, adj };
  }, [rows]);

  const openForm = (mode, row = null) => {
    if (row) overlay.openEdit(row);
    else overlay.openAdd({ mode });
  };

  const handleDelete = async (row) => {
    if (!await appConfirmMessage(`Delete movement ${row.code}?`)) return;
    try {
      await graphqlRequest(REMOVE_MOVEMENT, { id: row.id });
      toast.success('Movement deleted');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Failed to delete');
    }
  };

  const exportCsv = () => {
    const lines = [
      'Movement ID,Date,Type,Product,SKU,Qty,Site,Employee,Reference,Remark',
      ...filtered.map((r) =>
        [
          r.code,
          formatDate(r.date),
          r.type,
          `"${r.product}"`,
          r.sku,
          r.qty,
          `"${r.site}"`,
          `"${r.employee}"`,
          r.reference,
          `"${String(r.remark).replace(/"/g, '""')}"`,
        ].join(','),
      ),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'stock-movements.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="isk-page">
      <header className="isk-header">
        <h1>Stock Movements</h1>
        <div className="isk-actions">
          <button type="button" className="isk-btn isk-btn--in" onClick={() => openForm('in')}>
            <FaArrowDown /> Stock In
          </button>
          <button type="button" className="isk-btn isk-btn--out" onClick={() => openForm('out')}>
            <FaArrowUp /> Stock Out
          </button>
          <button type="button" className="isk-btn" onClick={() => openForm('adjust')}>
            Adjustment
          </button>
          <button type="button" className="isk-btn" onClick={() => openForm('transfer')}>
            Transfer
          </button>
          <button type="button" className="isk-btn" onClick={exportCsv}>
            <FaDownload /> Export
          </button>
        </div>
      </header>

      <section className="isk-stats">
        <article className="isk-stat">
          <div className="isk-stat-icon isk-stat-icon--green">
            <FaArrowDown />
          </div>
          <div>
            <div className="isk-stat-value">+{kpis.inn.toLocaleString()} units</div>
            <div className="isk-stat-label">Stock In This Month</div>
          </div>
        </article>
        <article className="isk-stat">
          <div className="isk-stat-icon isk-stat-icon--red">
            <FaArrowUp />
          </div>
          <div>
            <div className="isk-stat-value">-{kpis.out.toLocaleString()} units</div>
            <div className="isk-stat-label">Stock Out This Month</div>
          </div>
        </article>
        <article className="isk-stat">
          <div className="isk-stat-icon isk-stat-icon--blue">
            <FaWaveSquare />
          </div>
          <div>
            <div className="isk-stat-value">
              {kpis.net >= 0 ? '+' : ''}
              {kpis.net.toLocaleString()} units
            </div>
            <div className="isk-stat-label">Net Movement</div>
          </div>
        </article>
        <article className="isk-stat">
          <div className="isk-stat-icon isk-stat-icon--orange">
            <FaSyncAlt />
          </div>
          <div>
            <div className="isk-stat-value">{kpis.adj}</div>
            <div className="isk-stat-label">Adjustments</div>
          </div>
        </article>
      </section>

      <div className="isk-panel">
        <div className="isk-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`isk-tab ${tab === t.id ? 'is-active' : ''}`}
              onClick={() => overlay.setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="isk-filters">
          <label className="isk-search">
            <FaSearch />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search movements..."
            />
          </label>
          <select
            className="isk-select"
            value={site}
            onChange={(e) => setSite(e.target.value)}
          >
            {sites.map((s) => (
              <option key={s} value={s}>
                {s === 'All' ? 'All sites' : s}
              </option>
            ))}
          </select>
          <select
            className="isk-select"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {months.map((m) => (
              <option key={m} value={m}>
                {m === 'All' ? 'All dates' : m}
              </option>
            ))}
          </select>
          {(search || site !== 'All' || month !== 'All') && (
            <button
              type="button"
              className="isk-clear"
              onClick={() => {
                setSearch('');
                setSite('All');
                setMonth('All');
              }}
            >
              Clear Filters
            </button>
          )}
        </div>

        <div className="isk-table-wrap">
          <table className="isk-table">
            <thead>
              <tr>
                <th>Movement ID</th>
                <th>Date</th>
                <th>Type</th>
                <th>Product</th>
                <th>SKU</th>
                <th>Qty</th>
                <th>Site</th>
                <th>Employee</th>
                <th>Reference</th>
                <th>Remark</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" className="isk-empty">
                    Loading movements…
                  </td>
                </tr>
              ) : pageRows.length === 0 ? (
                <tr>
                  <td colSpan="11" className="isk-empty">
                    No movements yet. Use Stock In or Stock Out to record one.
                  </td>
                </tr>
              ) : (
                pageRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <button
                        type="button"
                        className="isk-id"
                        onClick={() => overlay.openView(row)}
                      >
                        {row.code}
                      </button>
                    </td>
                    <td>{formatDate(row.date)}</td>
                    <td>
                      <span className={`isk-pill isk-pill--${row.type.toLowerCase()}`}>
                        {row.type}
                      </span>
                    </td>
                    <td className="isk-name">{row.product}</td>
                    <td>{row.sku}</td>
                    <td className={row.type === 'TRF' ? '' : row.qty < 0 ? 'isk-qty--neg' : 'isk-qty--pos'}>
                      {row.type === 'TRF'
                        ? row.qty
                        : row.qty > 0
                          ? `+${row.qty}`
                          : row.qty}
                    </td>
                    <td>{row.site}</td>
                    <td>{row.employee}</td>
                    <td>{row.reference}</td>
                    <td>{row.remark}</td>
                    <td>
                      <div className="isk-actions-cell">
                        <button
                          type="button"
                          className="isk-icon-btn"
                          title="View"
                          onClick={() => overlay.openView(row)}
                        >
                          <FaEye />
                        </button>
                        <button
                          type="button"
                          className="isk-icon-btn"
                          title="Edit"
                          onClick={() => {
                            openForm(modeFromType(row.type), row);
                          }}
                        >
                          <FaEdit />
                        </button>
                        <button
                          type="button"
                          className="isk-icon-btn danger"
                          title="Delete"
                          onClick={() => void handleDelete(row)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="isk-foot">
          <span>
            Showing {pageRows.length} of {filtered.length} movements
          </span>
          <div className="isk-pages">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .slice(0, 8)
              .map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`isk-page-btn ${pageSafe === n ? 'is-active' : ''}`}
                  onClick={() => setPage(n)}
                >
                  {n}
                </button>
              ))}
          </div>
        </div>
      </div>

      {overlay.showForm ? (
        <StockRecordForm
          key={`${formMode}-${overlay.editing?.id || 'new'}`}
          mode={formMode}
          items={items}
          projects={projects}
          editData={overlay.editing?.edit || overlay.editing}
          onClose={overlay.closeOverlay}
          onSaved={() => {
            overlay.closeOverlay();
            void load();
          }}
        />
      ) : null}

      {overlay.viewing ? (
        <MovementDetailModal
          row={overlay.viewing}
          onClose={overlay.closeOverlay}
          onEdit={(row) => openForm(modeFromType(row.type), row)}
        />
      ) : null}
    </div>
  );
}
